import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  /** Current theme mode setting */
  mode: ThemeMode
  /** Set the theme mode */
  setMode: (mode: ThemeMode) => void
  /** Resolved theme (always 'light' or 'dark') */
  resolved: 'light' | 'dark'
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'theme'
const MEDIA_QUERY = '(prefers-color-scheme: dark)'

function getStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // localStorage may be unavailable
  }
  return 'light'
}

function getSystemPrefersDark(): boolean {
  return window.matchMedia(MEDIA_QUERY).matches
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemPrefersDark() ? 'dark' : 'light'
  }
  return mode
}

function applyThemeClass(resolved: 'light' | 'dark') {
  const el = document.documentElement
  if (resolved === 'dark') {
    el.classList.add('dark')
  } else {
    el.classList.remove('dark')
  }
}

export interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(getStoredMode)
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveTheme(mode))

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode)
    try {
      localStorage.setItem(STORAGE_KEY, newMode)
    } catch {
      // ignore
    }
  }, [])

  // Resolve and apply whenever mode changes
  useEffect(() => {
    const r = resolveTheme(mode)
    setResolved(r)
    applyThemeClass(r)
  }, [mode])

  // Listen for OS preference changes when in system mode
  useEffect(() => {
    if (mode !== 'system') return

    const mql = window.matchMedia(MEDIA_QUERY)
    const handler = () => {
      const r = resolveTheme('system')
      setResolved(r)
      applyThemeClass(r)
    }

    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [mode])

  return (
    <ThemeContext.Provider value={{ mode, setMode, resolved }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    return {
      mode: 'light',
      setMode: () => {
        console.warn('useTheme: ThemeProvider not found')
      },
      resolved: 'light',
    }
  }
  return context
}
